import {
  ActionSubmit,
  Form,
  LargeInput,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import { useNavigate } from "@tanstack/react-router"
import type { FormEventHandler } from "react"
import {
  useCallback,
  useState,
} from "react"

import styles from "./AcceptInvite.module.scss"
import { OnboardingPageTemplate } from "src/components"
import {
  useOverlayLoader,
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import {
  useRedeemFollowInvite,
  useUpdateFollow,
} from "src/hooks"
import { AcceptInvitationErrorToast } from "src/toasts/AcceptInvitationErrorToast"
import {
  getSugarmateError,
  injectProps,
} from "src/utils"

const codeLength = 6
const prefix = "pages.onboarding.accept_invite."

interface Props {
  backRoutePath: "/onboarding/get_started" | "/data_sharing/following";
  routePrefix: "/data_sharing/following" | "/onboarding";
}

export function AcceptInvite(props: Props) {
  const {
    routePrefix,
    backRoutePath,
  } = props

  const polyglot = usePolyglot()

  const navigate = useNavigate()

  const redeemInviteCode = useRedeemFollowInvite()

  const { showToast } = useToasterContext()

  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const { selfFollow } = useFollowedUsers()
  const selfFollowId = selfFollow.id

  const updateFollow = useUpdateFollow()

  const [
    confirmationCode,
    setConfirmationCode,
  ] = useState("")

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        try {
          if (!redeemInviteCode) {
            throw new Error("Unable to redeem invite code")
          }

          setIsOverlayLoaderVisible(true)

          const response = await redeemInviteCode(Number(confirmationCode))

          if (updateFollow && routePrefix === "/onboarding") {
            void updateFollow({
              id: selfFollowId,
              is_follow_only: true,
            }).catch() // do not show errors to user if this fails
          }

          void navigate({
            to: `${routePrefix}/accept_invite/$accountId/success`,
            replace: true,
            params: {
              accountId: response.data.follow.following_account_id.toString(),
            },
          })
        } catch (e) {
          const sugarmateError = getSugarmateError(e)

          const ErrorToast = injectProps(
            AcceptInvitationErrorToast,
            {
              code: sugarmateError?.code,
            },
          )

          showToast(
            "accept-invitation-error",
            ErrorToast,
          )

          setConfirmationCode("")
        } finally {
          setIsOverlayLoaderVisible(false)
        }
      })()
    },
    [
      routePrefix,
      confirmationCode,
      navigate,
      redeemInviteCode,
      showToast,
      setIsOverlayLoaderVisible,
      updateFollow,
      selfFollowId,
    ],
  )

  const onChangeConfirmationCode = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(
        /\D/g,
        "",
      ) // Allow only digits

      setConfirmationCode(value)

      if (value.length > codeLength) {
        setConfirmationCode(value.slice(
          0,
          codeLength,
        )) // Limit to codeLength
      }
    },
    [setConfirmationCode],
  )

  return (
    <OnboardingPageTemplate
      backRoutePath={backRoutePath}
      title={polyglot.t("components.page_header.pages.accept_invite")}
    >
      <Form
        className={styles.container}
        onSubmit={onSubmit}
      >
        <div
          className={styles.dosageNotice}
          id="dosage-notice"
        >
          {polyglot.t(`${prefix}dosage_notice`)}
        </div>
        <hr className={styles.hr} />
        <div className={styles.summary}>
          <div>
            {polyglot.t(`${prefix}description_one`)}
          </div>
          <div>
            {polyglot.t(`${prefix}description_two`)}
          </div>
          <div>
            {polyglot.t(`${prefix}description_three`)}
          </div>
          <div className={styles.semiBold}>
            {polyglot.t(`${prefix}description_four`)}
          </div>
        </div>
        <LargeInput
          maxLength={codeLength}
          placeholder={polyglot.t(`${prefix}confirmation_code_placeholder`)}
          inputMode="numeric"
          id="verification-code-field"
          type="text"
          className={styles.codeInput}
          value={confirmationCode}
          onChange={onChangeConfirmationCode}
          required
        />
        <ActionSubmit
          className={styles.button}
          id="accept-invitation-btn"
          value={polyglot.t(`${prefix}accept_invite_button.label`)}
        />
      </Form>
    </OnboardingPageTemplate>
  )
}
